import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import MobileCard from "@/components/mobile/MobileCard";
import FieldRow from "@/components/mobile/FieldRow";
import type { LineItem } from "../types/invoice";
import { convertToINR } from "@/helpers/Utils";
import { V2_COLORS } from "../constants/colors";

interface LineItemsCardProps {
  lineItems: LineItem[];
  /** Mobile (< md): render one flat card per line item instead of the table. */
  isMobile?: boolean;
}

// --- Styles (same pattern as ThreeWayMatchCard) ---

const thStyle = {
  fontWeight: 700,
  fontSize: "12px",
  lineHeight: "16px",
  color: V2_COLORS.textSecondary,
  textTransform: "uppercase" as const,
  whiteSpace: "nowrap" as const,
  bgcolor: "#F2F2F2",
  borderBottomWidth: 2,
  borderColor: "#E6E6E6",
  py: 1,
  px: 3,
};

const tdStyle = {
  fontSize: "14px",
  lineHeight: "20px",
  py: 1.5,
  px: 3,
  borderColor: "#E6E6E6",
};

const CARD_SX = { bgcolor: "background.paper", borderRadius: "8px", py: 2, pl: 1.5, pr: 3 };
const CARD_SX_MOBILE = { bgcolor: "background.paper", borderRadius: "8px", p: 1.5 };
const MOBILE_ROW_SX = { px: 0.5, py: 1.25, "&:last-of-type": { borderBottom: "none" } };

export default function LineItemsCard({ lineItems, isMobile = false }: LineItemsCardProps) {
  if (!lineItems.length) return null;

  const subtotal = lineItems.reduce((sum, item) => sum + item.amount, 0);
  const gst = subtotal * 0.18;
  const total = subtotal + gst;
  const summary: Array<[string, number]> = [
    ["Subtotal:", subtotal],
    ["GST 18%:", gst],
    ["Total:", total],
  ];

  return (
    <Box>
      <Typography fontWeight={700} fontSize="16px" lineHeight="24px" mb={1.5}>
        Line items
      </Typography>
      <Box sx={isMobile ? CARD_SX_MOBILE : CARD_SX}>
        {isMobile ? (
          <Box display="flex" flexDirection="column">
            {lineItems.map((item) => (
              <MobileCard key={item.id} flat sx={MOBILE_ROW_SX}>
                <Typography fontSize="14px" fontWeight={600} lineHeight="20px" mb={0.5}>
                  {item.description}
                </Typography>
                <Box display="flex" flexWrap="wrap" columnGap={1.5} rowGap={0.25}>
                  <FieldRow inline label="Qty" value={String(item.quantity)} />
                  <FieldRow inline label="Unit price" value={convertToINR(item.unit_price)} />
                  <FieldRow inline label="Amount" value={convertToINR(item.amount)} />
                </Box>
              </MobileCard>
            ))}
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...thStyle, width: 240 }}>Description</TableCell>
                  <TableCell sx={thStyle}>Qty</TableCell>
                  <TableCell sx={thStyle}>Unit Price</TableCell>
                  <TableCell sx={thStyle}>Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lineItems.map((item) => (
                  <TableRow key={item.id} sx={{ "&:last-child td": { borderBottom: 0 } }}>
                    <TableCell sx={tdStyle}>{item.description}</TableCell>
                    <TableCell sx={tdStyle}>{item.quantity}</TableCell>
                    <TableCell sx={tdStyle}>{convertToINR(item.unit_price)}</TableCell>
                    <TableCell sx={tdStyle}>{convertToINR(item.amount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Summary: one row on desktop, stacked label/value pairs on mobile */}
        <Box
          display="flex"
          flexDirection={isMobile ? "column" : "row"}
          justifyContent="flex-end"
          gap={isMobile ? 0.5 : 3}
          px={isMobile ? 0.5 : 3}
          pt={isMobile ? 1.5 : 2}
        >
          {summary.map(([label, value]) => (
            <Box key={label} display="flex" gap={1} justifyContent={isMobile ? "space-between" : "flex-start"}>
              <Typography fontSize="14px" fontWeight={500} color={V2_COLORS.textSecondary}>
                {label}
              </Typography>
              <Typography fontSize="14px">{convertToINR(value)}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}
